<template>
  <AsideGabarit :title="$t('filters.transportModes')" :action="action">
    <ToggleFilter v-if="study?.withWalkingTimes" :action="() => null" :caption="$t('travelTimeLimit.caption')">
      <template #icon>
        <i class="icon-clock" />
      </template>

      <template #active>
        <input id="maximumTraveTimeEnabled" v-model="maximumTravelTimeEnabled" type="checkbox" />
        <label class="switch" for="maximumTraveTimeEnabled" style="padding-left: 0; margin-bottom: 0">{{ $t('travelTimeLimit.enable') }}</label>
      </template>

      <template #body>
        <div class="description-outside">
          <div class="tag-line">{{ $t('travelTimeLimit.tagLine') }}</div>

          <div class="icon-container">
            <div class="more-info">i</div>

            <div class="more-info-popup">{{ $t('travelTimeLimit.moreInfo') }}</div>
          </div>
        </div>

        <div class="travel-time-limits-container">
          <TravelTimeLimit v-model="publicMaximumTravelTime" transportModeKey="public" :disabled="!maximumTravelTimeEnabled" />

          <TravelTimeLimit v-model="privateMaximumTravelTime" transportModeKey="private" :disabled="!maximumTravelTimeEnabled" />

          <TravelTimeLimit v-model="bicycleMaximumTravelTime" transportModeKey="bicycle" :disabled="!maximumTravelTimeEnabled" />

          <TravelTimeLimit v-model="walkingMaximumTravelTime" transportModeKey="walking" :disabled="!maximumTravelTimeEnabled" />
        </div>
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="!individualTransportOnly && study && study.customTransport"
      :caption="$t('transportMode.custom')"
      :override-active="currentMatchingTime === 'custom'"
      :description="$t('transportMode.customBaseline')"
      :action="() => updateTimeType('custom')"
    >
      <template #icon>
        <i class="icon-people_star" />
      </template>
      <template v-if="isDesktop || !(study.number_of_journeys > 50000)" #body>
        <div class="checkbox-holder" :class="{ disabled: currentMatchingTime !== 'custom' }">
          <input id="customPublic" v-model="transportFilter" type="checkbox" value="public" />
          <label for="customPublic">{{ $t('transportMode.public') }}</label>
        </div>
        <div class="checkbox-holder" :class="{ disabled: currentMatchingTime !== 'custom' }">
          <input id="customPrivate" v-model="transportFilter" type="checkbox" value="private" />
          <label for="customPrivate">{{ $t('transportMode.private') }}</label>
        </div>
        <div class="checkbox-holder" :class="{ disabled: currentMatchingTime !== 'custom' }">
          <input id="customBicycle" v-model="transportFilter" type="checkbox" value="bicycle" />
          <label for="customBicycle">{{ $t('transportMode.bicycle') }}</label>
        </div>
        <div v-if="study.withWalkingTimes" :class="{ disabled: currentMatchingTime !== 'custom' }" class="checkbox-holder">
          <input id="customWalking" v-model="transportFilter" type="checkbox" value="walking" />
          <label for="customWalking">{{ $t('transportMode.walking') }}</label>
        </div>
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="!individualTransportOnly && !personalizedTransportOnly"
      :caption="$t('transportMode.optimized')"
      :override-active="currentMatchingTime === 'optimized'"
      :description="$t('transportMode.optimizedBaseline')"
      :action="() => updateTimeType('optimized')"
    >
      <template #icon>
        <i class="icon-chrono" />
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="!individualTransportOnly && !personalizedTransportOnly"
      :caption="$t('transportMode.public')"
      :override-active="['public', 'public2025', 'public2030', 'public2027', 'public2031'].includes(currentMatchingTime)"
      :action="() => updateTimeType(currentPublic)"
      :class="{ disabled: !['public', 'public2025', 'public2030', 'public2027', 'public2031'].includes(currentMatchingTime) }"
    >
      <template #icon>
        <i class="icon-bus" />
      </template>

      <template v-if="futurTransportType > 0" #body>
        <div class="radio-holder">
          <input id="public" v-model="currentPublic" type="radio" value="public" @change="() => updateTimeType('public')" />
          <label for="public">{{ $t('transportMode.actuals') }}</label>
        </div>

        <div v-if="futurTransportType === 1" class="radio-holder">
          <input id="public2025" v-model="currentPublic" type="radio" value="public2025" @change="() => updateTimeType('public2025')" />
          <label for="public2025">{{ $t('transportMode.targetReach2025') }}</label>
        </div>
        <div v-if="futurTransportType === 2" class="radio-holder">
          <input id="public2030" v-model="currentPublic" type="radio" value="public2030" @change="() => updateTimeType('public2030')" />
          <label for="public2030">{{ $t('transportMode.targetReach2030') }}</label>
        </div>

        <div v-if="futurTransportType === 3" class="radio-holder">
          <input id="public2027" v-model="currentPublic" type="radio" value="public2027" @change="() => updateTimeType('public2027')" />
          <label for="public2027">{{ $t('transportMode.targetReach2027') }}</label>
        </div>
        <div v-if="futurTransportType === 4" class="radio-holder">
          <input id="public2031" v-model="currentPublic" type="radio" value="public2031" @change="() => updateTimeType('public2031')" />
          <label for="public2031">{{ $t('transportMode.targetReach2031') }}</label>
        </div>
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="!personalizedTransportOnly"
      :caption="$t('transportMode.private')"
      :override-active="['private', 'private_without_toll'].includes(currentMatchingTime)"
      :action="() => updateTimeType(currentPrivate)"
      :class="{ disabled: !['private', 'private_without_toll'].includes(currentMatchingTime) }"
    >
      <template #icon>
        <i class="icon-car" />
      </template>

      <template v-if="!carWithTollOnly" #body>
        <div class="radio-holder">
          <input id="private" v-model="currentPrivate" type="radio" value="private" @change="() => updateTimeType('private')" />
          <label for="private">{{ $t('transportMode.withToll') }}</label>
        </div>
        <div class="radio-holder">
          <input
            id="privateWithoutToll"
            v-model="currentPrivate"
            type="radio"
            value="private_without_toll"
            @change="() => updateTimeType('private_without_toll')"
          />
          <label for="privateWithoutToll">{{ $t('transportMode.withoutToll') }}</label>
        </div>
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="!individualTransportOnly && !personalizedTransportOnly"
      :caption="$t('transportMode.bicycle')"
      :override-active="currentMatchingTime === 'bicycle'"
      :action="() => updateTimeType('bicycle')"
    >
      <template #icon>
        <i class="icon-cycle" />
      </template>
    </ToggleFilter>

    <ToggleFilter
      v-if="study?.withWalkingTimes && !individualTransportOnly && !personalizedTransportOnly"
      :caption="$t('transportMode.walking')"
      :override-active="currentMatchingTime === 'walking'"
      :action="() => updateTimeType('walking')"
    >
      <template #icon>
        <i class="material-icons">directions_walk</i>
      </template>
    </ToggleFilter>

    <Transition name="fade">
      <div v-if="isFetching" class="loader-holder">
        <Loader />
      </div>
    </Transition>
  </AsideGabarit>
</template>

<script>
import { mapGetters } from 'vuex';
import AsideGabarit from './AsideGabarit';
import ToggleFilter from './ToggleFilter';
import TravelTimeLimit from './TravelTimeLimit';
import Loader from '@/components/Loader';
import { useSignalR } from '@dreamonkey/vue-signalr';

export default {
  components: {
    TravelTimeLimit,
    AsideGabarit,
    ToggleFilter,
    Loader,
  },

  props: ['action'],

  data() {
    return {
      currentPublic: 'public',
      currentPrivate: 'private',
    };
  },

  computed: {
    ...mapGetters(['filters', 'isDesktop', 'globalFilters', 'study', 'isFetching']),

    personalizedTransportOnly() {
      return this.study && this.study.personalized_transport_only;
    },

    individualTransportOnly() {
      return this.study && this.study.individual_transport_only;
    },

    currentMatchingTime() {
      return this.globalFilters && this.globalFilters.currentMatchingTime;
    },

    futurTransportType() {
      return this.study && this.study.futur_transport_type;
    },

    carWithTollOnly() {
      return this.study && this.study.car_with_toll_only;
    },

    transportFilter: {
      get: function () {
        return this.filters ? this.filters.transportType : [];
      },

      set: function (value) {
        this.updateTimeType('custom');

        this.$store.dispatch('updateFilters', {
          key: 'transportType',
          value: value,
        });
      },
    },

    maximumTravelTimeEnabled: {
      get() {
        return this.study.maximum_travel_time_enabled;
      },

      set(value) {
        this.$store.dispatch('updateMaximumTravelTime', { maximum_travel_time_enabled: value });
      },
    },

    publicMaximumTravelTime: {
      get() {
        return this.study.public_maximum_travel_time;
      },

      set(value) {
        this.$store.dispatch('updateMaximumTravelTime', { public_maximum_travel_time: value || 90 });
      },
    },

    privateMaximumTravelTime: {
      get() {
        return this.study.private_maximum_travel_time;
      },

      set(value) {
        this.$store.dispatch('updateMaximumTravelTime', { private_maximum_travel_time: value || 90 });
      },
    },

    bicycleMaximumTravelTime: {
      get() {
        return this.study.bicycle_maximum_travel_time;
      },

      set(value) {
        this.$store.dispatch('updateMaximumTravelTime', { bicycle_maximum_travel_time: value || 60 });
      },
    },

    walkingMaximumTravelTime: {
      get() {
        return this.study.walking_maximum_travel_time;
      },

      set(value) {
        this.$store.dispatch('updateMaximumTravelTime', { walking_maximum_travel_time: value || 60 });
      },
    },
  },

  created() {
    this.$socket = useSignalR();
  },

  methods: {
    updateTimeType(type) {
      if (type !== 'custom') {
        this.$store.dispatch('updateFilters', {
          key: 'transportType',
          value: [],
        });
      }

      this.$store
        .dispatch('updateGlobalFilters', {
          key: 'currentMatchingTime',
          value: type,
        })
        .then(() => {
          if (!this.isDesktop) {
            if (this.study && this.study.collaborators_total > 2000) {
              if (this.study.number_of_journeys < 50000) {
                const id = this.study.id || this.$route.params.studyId;
                this.$store.dispatch('uploadRawData', { id: id, content: this.$store.getters.screen() }).then(result => {
                  this.$socket.invoke('action', { action: 'tvBlob', parameters: result });
                });
              } else {
                this.$socket.invoke('action', { action: 'tvTransportType', parameters: type });
                this.$socket.invoke('action', { action: 'tvSites', parameters: this.$store.getters.screen().sites });
                this.$socket.invoke('action', {
                  action: 'tvAverageTime',
                  parameters: this.$store.getters.screen().averageTime,
                });
              }
            } else {
              this.$socket.invoke('action', { action: 'tvData', parameters: this.$store.getters.screen() });
            }
          }
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@use '@/styles/colors.scss';

:deep(.description) {
  padding-bottom: 12px;

  &:last-child {
    padding-bottom: 0;
  }
}

.checkbox-holder {
  &.disabled {
    [type='checkbox']:checked + label:before {
      border-color: colors.$nude-concrete-color;
    }
    [type='checkbox']:checked + label:after {
      background: colors.$nude-concrete-color;
    }
  }

  &:first-child {
    margin-top: 8px;
  }
}

.travel-time-limits-container {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.loader-holder {
  position: absolute;
}

.description-outside {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;

  .tag-line {
    color: colors.$nude-concrete-color;
    font-size: 11px;
    font-style: italic;
    margin: 0;
  }
}

.icon-container {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  position: relative;

  .more-info {
    width: 12px;
    height: 12px;
    border-radius: 12px;
    line-height: 12px;
    text-align: center;
    font-weight: bold;
    font-size: 12px;
    border: solid 1px #979797;
    color: #979797;
    cursor: pointer;
    padding: 2px;
    transition: 0.2s ease-in-out;

    &:hover {
      background: #979797;
      color: #ffffff;
    }
  }

  .more-info:hover + .more-info-popup {
    display: block;
  }

  .more-info-popup {
    display: none;
    position: absolute;
    background: white;
    z-index: 10;
    top: 24px;
    right: 0;
    font-size: 12px;
    padding: 6px;
    width: 200px;
    border: 1px solid #e3e3e3;
    border-radius: 2px;
    box-shadow: 0 2px 2px rgba(0, 0, 0, 0.3);
    transition: 0.2s ease-in-out;
  }
}
</style>
